import { OTHER_NODES_LABEL } from "@faire/help-center-self-serve/shared/other-node";
import { SelfServeNodes } from "@faire/help-center-self-serve/types";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Settings } from "@faire/web--source/slate/icons/foundation/Settings";
import React from "react";

import { AccountEmailIA } from "../instantAnswers/myAccount-accountEmail";
import { ContactInfoIA } from "../instantAnswers/myAccount-contactInfo";
import { EmailNotificationsIA } from "../instantAnswers/myAccount-emailNotifications";
import { LinkingPosIA } from "../instantAnswers/myAccount-linkingPos";
import { ManageTeamsIA } from "../instantAnswers/myAccount-manageTeams";
import { MultifactorAuthIA } from "../instantAnswers/myAccount-multifactorAuth";
import { resetOrUpdatePassword } from "../instantAnswers/myAccount-resetOrUpdatePassword";
import { ShippingAddressIA } from "../instantAnswers/myAccount-shippingAddress";
import { TroubleshootingAndOtherPOSIA } from "../instantAnswers/myAccount-troubleshootingAndOtherPos";
import { UnlinkingPosIA } from "../instantAnswers/myAccount-unlinkingPos";
import { UpdateAccountTypeIA } from "../instantAnswers/myAccount-updateAccountType";
import { VatAndTaxIdentifiersIA } from "../instantAnswers/myAccount-vatAndTaxIdentifiers";
import { PoBoxIA } from "../instantAnswers/ordersAndShipping-poBox";
import { BillingAddressIA } from "../instantAnswers/shared-billingAddress";
import { VatAndTaxIdentifiersVisibilityFn } from "../visibilityFunctions/vatAndTaxIdentifiersVisibilityFn";

export const useSettingsRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    SettingsAndAccount: {
      id: "SettingsAndAccount",
      label: strictLocalize({
        defaultMessage: "Settings & Account",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Settings fontSize="24px" color="faireBlack" />,
      children: ["AccountInformation", "ManageTeam", "PosIntegration"],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Settings issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    AccountInformation: {
      id: "AccountInformation",
      label: strictLocalize({
        defaultMessage: "Account information",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "LoginAndSecurity",
        "NotificationPreferences",
        "Addresses",
        "ContactInformation",
        "Tax Information",
        "UpdateAccountType",
        "OtherAccountInformation",
      ],
      level: 2,
    },
    ManageTeam: {
      id: "ManageTeam",
      label: strictLocalize({
        defaultMessage: "Manage team",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.MANAGE_TEAMS],
      instantAnswer: ManageTeamsIA(strictLocalize),
      level: 2,
    },
    PosIntegration: {
      id: "PosIntegration",
      label: strictLocalize({
        defaultMessage: "POS Integration",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "LinkPos",
        "TroubleshootPosIntegration",
        "UnlinkPos",
        "OtherPosIntegration",
      ],
      level: 2,
    },

    LoginAndSecurity: {
      id: "LoginAndSecurity",
      label: strictLocalize({
        defaultMessage: "Login & security",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["LoginEmail", "ResetOrUpdatePassword", "MultifactorAuth"],
      level: 3,
    },
    NotificationPreferences: {
      id: "NotificationPreferences",
      label: strictLocalize({
        defaultMessage: "Notification preferences",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.EMAIL_NOTIFICATIONS],
      instantAnswer: EmailNotificationsIA(strictLocalize),
      level: 3,
    },
    Addresses: {
      id: "Addresses",
      label: strictLocalize({
        defaultMessage: "Addresses",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["BillingAddress", "ShippingAddress", "RestrictedAddresses"],
      level: 3,
    },

    ContactInformation: {
      id: "ContactInformation",
      label: strictLocalize({
        defaultMessage: "Contact information",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ContactInfoIA(strictLocalize),
      level: 3,
    },
    TaxInformation: {
      id: "TaxInformation",
      label: strictLocalize({
        defaultMessage: "Tax information",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["TaxIDs"],
      level: 3,
    },
    UpdateAccountType: {
      id: "UpdateAccountType",
      label: strictLocalize({
        defaultMessage: "Update account type",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: UpdateAccountTypeIA(strictLocalize),
      level: 3,
    },
    OtherAccountSettings: {
      id: "OtherAccountSettings",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [
        articles.GettingStarted.RESELLER_ID,
        articles.GettingStarted.BRICK_AND_MORTAR,
        articles.MyOrders.BUSINESS_PROFILE,
      ],
      level: 3,
    },
    LinkPos: {
      id: "LinkPos",
      label: strictLocalize({
        defaultMessage: "Link POS",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: LinkingPosIA(strictLocalize),
      level: 3,
    },
    TroubleshootPosIntegration: {
      id: "TroubleshootPosIntegration",
      label: strictLocalize({
        defaultMessage: "Troubleshoot POS integration",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: TroubleshootingAndOtherPOSIA(strictLocalize),
      level: 3,
    },
    UnlinkPos: {
      id: "UnlinkPos",
      label: strictLocalize({
        defaultMessage: "Unlink POS",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: UnlinkingPosIA(strictLocalize),
      level: 3,
    },
    OtherPosIntegration: {
      id: "OtherPosIntegration",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.MyOrders.SYNC_SQUARE_POS],
      instantAnswer: TroubleshootingAndOtherPOSIA(strictLocalize),
      level: 3,
    },
    ResetOrUpdatePassword: {
      id: "ResetOrUpdatePassword",
      label: strictLocalize({
        defaultMessage: "Reset or update password",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.FAQ.RESETTING_PASSWORD,
        articles.FAQ.FAIRE_ACCOUNT_SECURITY,
      ],
      instantAnswer: resetOrUpdatePassword(strictLocalize),
      level: 4,
    },
    LoginEmail: {
      id: "LoginEmail",
      label: strictLocalize({
        defaultMessage: "Login email",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.ACCOUNT_EMAIL],
      instantAnswer: AccountEmailIA(strictLocalize),
      level: 4,
    },
    MultifactorAuth: {
      id: "MultifactorAuth",
      label: strictLocalize({
        defaultMessage: "Multifactor authentication",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: MultifactorAuthIA(strictLocalize),
      level: 4,
    },
    BillingAddress: {
      id: "BillingAddress",
      label: strictLocalize({
        defaultMessage: "Billing address",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_CHANGE_MY_BILLING_ADDRESS,
      ],
      instantAnswer: BillingAddressIA(strictLocalize),
      level: 4,
    },
    ShippingAddress: {
      id: "ShippingAddress",
      label: strictLocalize({
        defaultMessage: "Shipping address",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: ShippingAddressIA(strictLocalize),
      level: 4,
    },
    RestrictedAddresses: {
      id: "RestrictedAddresses",
      label: strictLocalize({
        defaultMessage: "Restricted addresses",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: PoBoxIA(strictLocalize),
      level: 4,
    },
    TaxIDs: {
      id: "TaxIDs",
      label: strictLocalize({
        defaultMessage: "Tax IDs",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.VAT],
      instantAnswer: VatAndTaxIdentifiersIA(strictLocalize),
      isNodeVisible: VatAndTaxIdentifiersVisibilityFn,
      level: 4,
    },
  };
};
